/* eslint-disable react-refresh/only-export-components -- провайдер и его хук
   живут вместе: контекст между ними не должен утекать наружу. */
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useSession } from '../../app/session'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { toProduct } from '../catalog/api'
import { isApiError, type components } from '@dorixona/api'
import {
  cartCount,
  changeQuantity,
  guestLines,
  keepOrder,
  quantityOf,
  setQuantity,
  type CartMap,
} from './model'
import {
  CART_MAX_QTY,
  EMPTY_CART,
  useCart as useServerCart,
  useClearCart,
  useMergeCart,
  useSetQuantity,
  type CartIssue,
  type CartLine,
  type GuestLine,
} from './api'

export interface Cart {
  lines: CartLine[]
  count: number
  goods: number
  /** Кэшбэк, если заплатить всю сумму деньгами. */
  cashback: number
  hasRx: boolean
  /** Что мешает оформить: товар сняли с витрины или он кончился. */
  issues: CartIssue[]
  /** Строки ещё едут: корзина не пуста, просто не пришла. */
  loading: boolean
  quantityOf: (productId: number) => number
  setQuantity: (productId: number, quantity: number) => void
  /**
   * «+» и «−». `max` — сколько даёт одна аптека (`max_qty` товара или строки):
   * выше него «+» не поднимает, сервер такое всё равно отклонил бы (R-26).
   */
  changeQuantity: (productId: number, delta: number, max?: number) => void
  /** Потолок «+» с учётом свежего отказа сервера: он знает остаток лучше экрана. */
  maxOf: (productId: number, max: number) => number
  /** Сервер не дал столько (409 `out_of_stock`) — сколько можно, говорит счётчик. */
  notice: CartNotice | null
  /**
   * Положить несколько товаров разом (мультипоиск): количества прибавляются к
   * тому, что уже лежит. У гостя — одна запись в браузер, у вошедшего — один
   * запрос `POST /cart/merge`, который так и складывает количества.
   */
  addMany: (lines: GuestLine[]) => Promise<void>
  clear: () => void
}

export interface CartNotice {
  productId: number
  available: number
}

const CartContext = createContext<Cart | null>(null)

export const CART_STORAGE_KEY = 'dorixona.cart'

/**
 * Пауза после последнего нажатия «+/−», после которой количество уходит на
 * сервер. Цифра на экране меняется сразу; серия быстрых нажатий — один запрос.
 */
export const SAVE_DELAY_MS = 300

/** Сервер принимает от 0 (строку убрать) до 99 штук. */
const clampQty = (qty: number) => Math.max(0, Math.min(CART_MAX_QTY, qty))

/** Сколько висит «Доступно N шт.»: прочитать успевают, экран не засоряется. */
export const NOTICE_MS = 3000

/**
 * Количество после «+»/«−». Выше потолка «+» не поднимает. Если строки уже
 * больше потолка (остаток упал), «+» ничего не делает, а «−» сразу опускает до
 * потолка: сервер отклоняет всё, что выше него, и шаг по одной откатывался бы
 * обратно — с 84 до 19 было бы не дойти.
 */
export function nextQuantity(current: number, delta: number, max: number): number {
  const next = current + delta
  if (delta > 0) return clampQty(Math.min(next, Math.max(max, current)))
  return clampQty(current > max ? Math.min(next, max) : next)
}

/** Гостевая корзина переживает перезагрузку: покупатель возвращается к тому же. */
export function readCart(storage: Storage): CartMap {
  const raw = storage.getItem(CART_STORAGE_KEY)
  if (!raw) return {}
  try {
    return JSON.parse(raw) as CartMap
  } catch {
    return {}
  }
}

export interface CartProviderProps {
  children: ReactNode
  storage?: Storage
}

/**
 * Корзина одна на приложение, источник у неё два: сервер для вошедшего и
 * браузер для гостя. При входе гостевая корзина доливается в серверную —
 * ровно один раз, иначе количества сложились бы дважды.
 */
export function CartProvider({ children, storage = localStorage }: CartProviderProps) {
  const { isAuthenticated } = useSession()
  const api = useApi()
  const [guest, setGuest] = useState<CartMap>(() => readCart(storage))

  const server = useServerCart(isAuthenticated)
  const save = useSetQuantity()
  const clearServer = useClearCart()
  const merge = useMergeCart()
  const merged = useRef(false)
  const client = useQueryClient()

  // Количества, которые покупатель уже нажал, а сервер ещё не подтвердил.
  // Ref — для таймера и ответа: им нужно последнее значение, а не то, что
  // было при рендере.
  const [pending, setPending] = useState<Record<number, number>>({})
  const pendingRef = useRef(pending)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())
  const lastRequest = useRef(0)
  const lineOrder = useRef<number[]>([])
  const [notice, setNotice] = useState<CartNotice | null>(null)

  useEffect(() => {
    if (!notice) return
    const timer = setTimeout(() => {
      setNotice(null)
    }, NOTICE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [notice])

  /**
   * Сервер ответил 409 `out_of_stock`: пока товар лежал на экране, его разобрали.
   * Сколько осталось, в ошибке нет — берём свежий `max_qty` товара и обновляем
   * карточки, чтобы «+» упёрся в новый потолок. Товар не пришёл — числа не
   * выдумываем: счётчик и так вернётся к тому, что лежит на сервере.
   */
  const refuse = useCallback(
    async (productId: number) => {
      const fresh = await api
        .get<components['schemas']['Product']>(`/catalog/products/${productId}`)
        .then((dto) => toProduct(dto).maxQty)
        .catch(() => null)
      if (fresh !== null) setNotice({ productId, available: fresh })
      void client.invalidateQueries({ queryKey: ['products'] })
    },
    [api, client],
  )

  const updatePending = useCallback((next: Record<number, number>) => {
    pendingRef.current = next
    setPending(next)
  }, [])

  /** Ответ последнего запроса пришёл: всё, что не ждёт отправки, уже на сервере. */
  const settle = useCallback(() => {
    updatePending(
      Object.fromEntries(
        Object.entries(pendingRef.current).filter(([id]) => timers.current.has(Number(id))),
      ),
    )
  }, [updatePending])

  const queue = useCallback(
    (productId: number, qty: number) => {
      updatePending({ ...pendingRef.current, [productId]: qty })
      clearTimeout(timers.current.get(productId))
      timers.current.set(
        productId,
        setTimeout(() => {
          timers.current.delete(productId)
          const request = ++lastRequest.current
          save
            .mutateAsync({ productId, qty: pendingRef.current[productId] as number })
            .then((cart) => {
              // Пока этот ответ шёл, ушёл следующий запрос — итог возьмём из него,
              // иначе старый ответ откатил бы уже нажатое.
              if (request !== lastRequest.current) return
              client.setQueryData(keys.cart, cart)
              settle()
            })
            .catch((error: unknown) => {
              if (isApiError(error) && error.code === 'out_of_stock') void refuse(productId)
              if (request !== lastRequest.current) return
              // Сервер не принял — показываем то, что у него на самом деле.
              settle()
              void client.invalidateQueries({ queryKey: keys.cart })
            })
        }, SAVE_DELAY_MS),
      )
    },
    [client, refuse, save, settle, updatePending],
  )

  const persist = useCallback(
    (next: CartMap) => {
      setGuest(next)
      storage.setItem(CART_STORAGE_KEY, JSON.stringify(next))
    },
    [storage],
  )

  // Гостевые строки показываем по данным каталога: цена и наличие должны быть
  // сегодняшними, а не теми, что лежали в браузере с прошлого раза.
  const ids = useMemo(() => guestLines(guest).map((line) => line.productId), [guest])
  const guestProducts = useQueries({
    queries: ids.map((id) => ({
      queryKey: keys.product(id),
      queryFn: async () =>
        toProduct(await api.get<components['schemas']['Product']>(`/catalog/products/${id}`)),
      enabled: !isAuthenticated,
    })),
  })

  useEffect(() => {
    if (!isAuthenticated || merged.current) return
    const lines = guestLines(guest)
    merged.current = true
    if (lines.length === 0) return
    merge.mutate(lines, {
      onSettled: () => {
        persist({})
      },
    })
  }, [isAuthenticated, guest, merge, persist])

  const value = useMemo<Cart>(() => {
    const maxOf = (productId: number, max: number) =>
      Math.min(max, notice?.productId === productId ? notice.available : CART_MAX_QTY)

    if (isAuthenticated) {
      const cart = server.data ?? EMPTY_CART
      const serverQty = (productId: number) =>
        cart.lines.find((line) => line.productId === productId)?.qty ?? 0
      const quantity = (productId: number) => pending[productId] ?? serverQty(productId)
      // Строки — с тем количеством, что уже нажато; цену строки знаем, поэтому
      // сумму и кэшбэк двигаем на разницу, не дожидаясь сервера.
      const [ordered, order] = keepOrder(cart.lines, lineOrder.current)
      lineOrder.current = order
      const lines = ordered
        .map((line) => {
          const qty = quantity(line.productId)
          return { ...line, qty, total: line.price * qty }
        })
        .filter((line) => line.qty > 0)
      const shift = (line: CartLine) => (quantity(line.productId) - line.qty) * line.price
      const unseen = Object.entries(pending)
        .filter(([id]) => !cart.lines.some((line) => line.productId === Number(id)))
        .reduce((sum, [, qty]) => sum + qty, 0)
      return {
        lines,
        count: lines.reduce((sum, line) => sum + line.qty, 0) + unseen,
        goods: cart.goods + cart.lines.reduce((sum, line) => sum + shift(line), 0),
        cashback:
          cart.cashback +
          Math.round(cart.lines.reduce((sum, line) => sum + (shift(line) * line.cb) / 100, 0)),
        hasRx: cart.hasRx,
        issues: cart.issues,
        loading: server.isPending,
        quantityOf: quantity,
        setQuantity: (productId, qty) => {
          queue(productId, clampQty(qty))
        },
        changeQuantity: (productId, delta, max = CART_MAX_QTY) => {
          const current = quantity(productId)
          const next = nextQuantity(current, delta, maxOf(productId, max))
          if (next !== current) queue(productId, next)
        },
        maxOf,
        notice,
        addMany: async (lines) => {
          await merge.mutateAsync(lines)
        },
        clear: () => {
          // Нажатое, но не отправленное уже не нужно: корзина пустеет целиком.
          timers.current.forEach(clearTimeout)
          timers.current.clear()
          updatePending({})
          clearServer.mutate()
        },
      }
    }

    const lines: CartLine[] = guestLines(guest).flatMap((line, i) => {
      const product = guestProducts[i]?.data
      if (!product) return []
      return [
        {
          productId: product.id,
          name: product.name,
          price: product.price,
          qty: line.qty,
          total: product.price * line.qty,
          cb: product.cb,
          rx: product.rx,
          active: true,
          inStock: product.inStock,
          maxQty: product.maxQty,
          photo: product.photos[0] ?? '',
        },
      ]
    })
    const goods = lines.reduce((sum, line) => sum + line.total, 0)
    return {
      lines,
      count: cartCount(guest),
      goods,
      // Тот же порядок округления, что на сервере: процент строки, потом сумма.
      cashback: Math.round(lines.reduce((sum, line) => sum + (line.total * line.cb) / 100, 0)),
      hasRx: lines.some((line) => line.rx),
      // У гостя корзина ещё не проверялась сервером: он о ней не знает.
      issues: [],
      loading: guestProducts.some((query) => query.isPending) && ids.length > 0,
      quantityOf: (productId) => quantityOf(guest, productId),
      setQuantity: (productId, quantity) => {
        persist(setQuantity(guest, productId, quantity))
      },
      changeQuantity: (productId, delta, max = CART_MAX_QTY) => {
        const current = quantityOf(guest, productId)
        const next = nextQuantity(current, delta, maxOf(productId, max))
        if (next !== current) persist(setQuantity(guest, productId, next))
      },
      maxOf,
      // Гостевую корзину сервер не проверяет: отказа быть не может.
      notice: null,
      addMany: (lines) => {
        // Одной записью: по одной каждая затёрла бы предыдущую — у всех один `guest`.
        persist(lines.reduce((cart, line) => changeQuantity(cart, line.productId, line.qty), guest))
        return Promise.resolve()
      },
      clear: () => {
        persist({})
      },
    }
  }, [
    isAuthenticated,
    server.data,
    server.isPending,
    pending,
    queue,
    updatePending,
    clearServer,
    merge,
    guest,
    guestProducts,
    ids,
    persist,
    notice,
  ])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): Cart {
  const cart = useContext(CartContext)
  if (!cart) throw new Error('useCart вызван вне CartProvider')
  return cart
}
